import React, { useEffect, useRef, useState } from 'react';

import {
  Popper,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import { Instance } from '@popperjs/core/lib';
import clsx from 'clsx';
import ReactDOM from 'react-dom';

import useAlertRects from './useAlertRects';
import useAppendCustomClassForContainer from '../../../hooks/useAppendCustomClassForContainer';
import getUnderlineColor from '../../../utils/getUnderlineColor';
import AlertPopover from '../../AlertPopover';
import { HighlighterProps } from '../types';

const Highlighter: React.FC<HighlighterProps> = ({
  checkableElement,
  targetEl,
  alerts,
  onInsert,
  onDismiss,
}) => {
  const popperRef = useRef<Instance>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { rects } = useAlertRects(
    checkableElement,
    targetEl,
    container,
    alerts,
  );

  useEffect(() => {
    if (targetEl) {
      const highlighterContainer = checkableElement.getHighlighterContainer();
      if (containerRef.current !== highlighterContainer) {
        setContainer(highlighterContainer);
        containerRef.current = highlighterContainer;
      }
    }
  }, [targetEl, checkableElement]);

  const referenceElement = container || targetEl;
  useAppendCustomClassForContainer();

  const highlightComponent = (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
      id="justdone-grammar-highlighter"
    >
      {rects.map((alertRects, i) =>
        alertRects.map((rect, j) => {
          if (!alerts.at(i)) {
            return null;
          }
          const alert = alerts[i];
          const category = alert?.category?.toLowerCase().replace(' ', '-');
          const key = `${alert.absOffset}-${alert.length}-${i}-${j}`;

          return (
            <TransparentTooltip
              data-justdone-highlight={key}
              key={key}
              title={
                <AlertPopover
                  alert={alerts[i]}
                  alertRect={rects[i][j]}
                  onInsert={(replacement): void => {
                    onInsert?.(alerts[i], replacement);
                  }}
                  onDismiss={(): void => {
                    onDismiss?.(alerts[i]);
                  }}
                  targetEl={referenceElement}
                  containerEl={container}
                />
              }
            >
              <div
                className={clsx(
                  'justdone-grammar-highlight',
                  `justdone-grammar-highlight-category-${category}`,
                )}
                style={{
                  position: 'absolute',
                  top: rect.top,
                  left: rect.left,
                  width: rect.width,
                  height: rect.height,
                  borderBottom: `2px solid ${getUnderlineColor(alerts[i])}`,
                }}
                onMouseDown={(e): void => e.preventDefault()}
              />
            </TransparentTooltip>
          );
        }),
      )}
    </div>
  );

  return referenceElement ? (
    <>
      <Popper
        disablePortal
        popperRef={popperRef}
        anchorEl={referenceElement}
        popperOptions={{
          strategy: 'absolute',
        }}
        placement="top-start"
        open
        modifiers={[
          {
            name: 'flip',
            options: {
              fallbackPlacements: [],
            },
          },
        ]}
      >
        {container && ReactDOM.createPortal(highlightComponent, container)}
      </Popper>
    </>
  ) : null;
};

const TransparentTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    color: 'inherit',
    border: 'none',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
  },
}));

export default Highlighter;
